/**
 * Modal for creating / editing a registry entry.
 * Design matches the original CreateSystemModal style.
 *
 * Infrastructure binding: the entry's CODE is used automatically by the discovery
 * service (system_mapper.go) to match pods/namespaces. Here we only record WHAT
 * type of k8s resource this entry represents (pod-level or namespace-level).
 *
 * For namespace binding: user also picks a specific namespace; the modal then shows
 * its status, labels, and available pods.
 */
import React, { useState, useEffect, useCallback } from 'react';
import EditIcon from '@mui/icons-material/EditOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CableIcon from '@mui/icons-material/Cable';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { listClusters, listNamespaces } from '../../../api/discoveryApi';
import { listEntries, listRelationTypes, listRelations, createRelation, deleteRelation } from '../../../api/metaRegistriesApi';
import { DEFAULT_REGISTRY_UI_PROFILE } from '../registryUiProfile';

const EntryModal = ({
  isOpen,
  onClose,
  onSubmit,
  theme,
  kinds,
  parentEntry,        // pre-set parent (when creating child)
  allEntries,         // flat list for parent picker
  initialValues,      // for edit mode
  title,
  submitLabel,
  uiProfile,
  projectId,
}) => {
  const isEdit = !!initialValues;
  const profile = uiProfile || DEFAULT_REGISTRY_UI_PROFILE;
  const infrastructureBindingEnabled = profile.features.infrastructureBinding;
  const tagNames = isEdit && initialValues.tags
    ? initialValues.tags.map(t => {
        const n = t.name || t;
        const c = t.color ? `:${t.color}` : '';
        return `${n}${c}`;
      }).join(', ')
    : '';

  const [form, setForm] = useState({
    code: '',
    name: '',
    description: '',
    kind_slug: '',
    tags: '',
    parent_id: null,
    k8s_binding: 'none', // none | namespace | pod
    k8s_cluster_id: '',
    k8s_namespace: '',
    attributes: {},
  });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  // BC capability binding
  const bcCapabilityBindingEnabled = profile.features.bcCapabilityBinding;
  const [bcEntries, setBcEntries] = useState([]); // all BC entries for picker
  const [bcLoading, setBcLoading] = useState(false);
  const [implementsTypeId, setImplementsTypeId] = useState(null);
  const [selectedBcIds, setSelectedBcIds] = useState([]); // UUIDs currently selected
  const [originalBcLinks, setOriginalBcLinks] = useState({}); // { bcEntryId: relationId }
  const [bcSearch, setBcSearch] = useState('');

  // Namespace picker state
  const [nsOptions, setNsOptions] = useState([]); // [{clusterId, clusterName, name, status, labels}]
  const [nsOptionsLoading, setNsOptionsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit && initialValues) {
      const attrs = initialValues.attributes || {};
      setForm({
        code: initialValues.code || '',
        name: initialValues.name || '',
        description: initialValues.description || '',
        kind_slug: initialValues.kind_slug || '',
        tags: tagNames,
        parent_id: initialValues.parent_id || null,
        k8s_binding: infrastructureBindingEnabled ? (attrs.k8s_resource_type || 'none') : 'none',
        k8s_cluster_id: infrastructureBindingEnabled ? (attrs.k8s_cluster_id || '') : '',
        k8s_namespace: infrastructureBindingEnabled ? (attrs.k8s_namespace || '') : '',
        attributes: { ...attrs },
      });
    } else {
      setForm({
        code: '',
        name: '',
        description: '',
        kind_slug: '',
        tags: '',
        parent_id: parentEntry?.id || null,
        k8s_binding: 'none',
        k8s_cluster_id: '',
        k8s_namespace: '',
        attributes: {},
      });
    }
    setNsOptions([]);
    setError('');
  }, [isOpen, infrastructureBindingEnabled]);

  useEffect(() => {
    if (!isOpen || !bcCapabilityBindingEnabled) {
      setBcEntries([]);
      setSelectedBcIds([]);
      setOriginalBcLinks({});
      setImplementsTypeId(null);
      setBcSearch('');
      return;
    }
    let cancelled = false;
    setBcLoading(true);
    (async () => {
      try {
        const [entries, types] = await Promise.all([
          listEntries(projectId, 'business-capabilities', { all: true }),
          listRelationTypes(projectId),
        ]);
        if (cancelled) return;
        setBcEntries(entries);
        const implType = types.find(t => t.slug === 'implements');
        setImplementsTypeId(implType?.id ?? null);

        if (isEdit && initialValues?.id && implType) {
          const relations = await listRelations(projectId, { from: initialValues.id });
          if (cancelled) return;
          const links = {};
          const ids = [];
          for (const rel of relations) {
            if (rel.relation_type?.slug === 'implements') {
              links[rel.to_entry_id] = rel.id;
              ids.push(rel.to_entry_id);
            }
          }
          setOriginalBcLinks(links);
          setSelectedBcIds(ids);
        }
      } catch (e) {
        // non-fatal: BC binding section will be empty
        console.error('BC binding load error:', e);
      } finally {
        if (!cancelled) setBcLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, bcCapabilityBindingEnabled, isEdit, initialValues?.id]);

  // Load namespaces across all clusters when namespace binding is selected
  useEffect(() => {
    if (!isOpen || !infrastructureBindingEnabled || form.k8s_binding !== 'namespace') {
      setNsOptions([]);
      setNsOptionsLoading(false);
      return;
    }
    let cancelled = false;
    setNsOptionsLoading(true);
    (async () => {
      try {
        const clusters = await listClusters(projectId);
        const all = [];
        await Promise.all(
          clusters.map(async (cl) => {
            try {
              const nsList = await listNamespaces(projectId, cl.id);
              for (const ns of nsList) {
                if (!ns.is_deleted) {
                  all.push({ clusterId: cl.id, clusterName: cl.name || cl.id, ...ns });
                }
              }
            } catch {
              // cluster unreachable — skip
            }
          })
        );
        if (!cancelled) setNsOptions(all);
      } catch {
        if (!cancelled) setNsOptions([]);
      } finally {
        if (!cancelled) setNsOptionsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, infrastructureBindingEnabled, form.k8s_binding]);

  const handleChange = useCallback((field, value) => {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      // Reset downstream when binding type changes
      if (field === 'k8s_binding') {
        next.k8s_cluster_id = '';
        next.k8s_namespace = '';
      }
      // Reset namespace when cluster changes
      if (field === 'k8s_cluster_id') {
        next.k8s_namespace = '';
      }
      return next;
    });
    setError('');
  }, []);

  const handleNsSelect = useCallback((e) => {
    const val = e.target.value; // "clusterId|namespace"
    if (!val) {
      setForm(prev => ({ ...prev, k8s_cluster_id: '', k8s_namespace: '' }));
      setError('');
      return;
    }
    const [cid, ns] = val.split('|');
    // Auto-fill code with namespace name (item 1)
    setForm(prev => ({ ...prev, k8s_cluster_id: cid, k8s_namespace: ns, code: ns }));
    setError('');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.code.trim() || !form.name.trim()) {
      setError('Код и название обязательны для заполнения');
      return;
    }
    // Validate code matches namespace (item 2)
    if (
      !isEdit &&
      infrastructureBindingEnabled &&
      form.k8s_binding === 'namespace' &&
      form.k8s_namespace &&
      form.code.trim() !== form.k8s_namespace
    ) {
      setError(`Код должен совпадать с именем namespace: "${form.k8s_namespace}"`);
      return;
    }
    setCreating(true);
    setError('');
    try {
      // Parse tags
      const tags = form.tags
        .split(',')
        .map(t => t.trim())
        .filter(Boolean)
        .map(t => {
          const parts = t.split(':');
          if (parts.length === 2 && parts[1].startsWith('#')) {
            return { name: parts[0].trim(), color: parts[1].trim() };
          }
          return { name: t };
        });

      // Build attributes
      const attributes = { ...(form.attributes || {}) };
      if (infrastructureBindingEnabled) {
        if (form.k8s_binding !== 'none') {
          attributes.k8s_resource_type = form.k8s_binding;
          if (form.k8s_binding === 'namespace') {
            if (form.k8s_cluster_id) attributes.k8s_cluster_id = form.k8s_cluster_id;
            else delete attributes.k8s_cluster_id;
            if (form.k8s_namespace) attributes.k8s_namespace = form.k8s_namespace;
            else delete attributes.k8s_namespace;
          } else {
            delete attributes.k8s_cluster_id;
            delete attributes.k8s_namespace;
          }
        } else {
          delete attributes.k8s_resource_type;
          delete attributes.k8s_cluster_id;
          delete attributes.k8s_namespace;
        }
      }

      await onSubmit({
        code: form.code.trim(),
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        kind_slug: form.kind_slug || undefined,
        tags: tags.length > 0 ? tags : undefined,
        parent_id: form.parent_id || null,
        attributes: Object.keys(attributes).length > 0 ? attributes : undefined,
      });

      // Save BC capability links (edit mode only)
      if (isEdit && bcCapabilityBindingEnabled && implementsTypeId) {
        const added = selectedBcIds.filter(id => !originalBcLinks[id]);
        const removed = Object.entries(originalBcLinks)
          .filter(([bcId]) => !selectedBcIds.includes(bcId))
          .map(([, relId]) => relId);
        await Promise.all([
          ...added.map(bcId =>
            createRelation(projectId, {
              from_entry_id: initialValues.id,
              to_entry_id: bcId,
              relation_type_id: implementsTypeId,
            })
          ),
          ...removed.map(relId => deleteRelation(projectId, relId)),
        ]);
      }

      onClose();
    } catch (err) {
      setError(err.message || 'Ошибка при сохранении');
    } finally {
      setCreating(false);
    }
  };

  if (!isOpen) return null;

  const isDark = theme.name === 'dark';
  const overlay = {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 5000
  };
  const modal = {
    background: isDark ? '#1a1f2e' : '#ffffff',
    borderRadius: 12, padding: 24,
    width: 'min(560px, 92vw)', maxHeight: '92vh', overflow: 'auto',
    boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
    border: `1px solid ${theme.toolbar.buttonBorder}`
  };
  const label = {
    display: 'block', fontSize: 13, fontWeight: 600,
    color: theme.toolbar.text, marginBottom: 4, marginTop: 14
  };
  const input = {
    width: '100%', padding: '8px 10px', borderRadius: 6,
    border: `1px solid ${theme.toolbar.buttonBorder}`,
    background: theme.toolbar.buttonBackground,
    color: theme.toolbar.text, fontSize: 14, outline: 'none', boxSizing: 'border-box'
  };
  const hint = { fontSize: 11, opacity: 0.6, marginTop: 3, color: theme.toolbar.text };
  const btn = {
    padding: '9px 18px', borderRadius: 6, fontSize: 14,
    fontWeight: 600, cursor: 'pointer', flex: 1
  };
  const primaryBtn = {
    ...btn, background: isDark ? '#3b82f6' : '#2563eb', color: '#fff', border: 'none'
  };
  const secondaryBtn = {
    ...btn, background: theme.toolbar.buttonBackground,
    color: theme.toolbar.text, border: `1px solid ${theme.toolbar.buttonBorder}`
  };

  // Allowed kinds for placement
  const allowedKinds = parentEntry
    ? kinds.filter(k => (k.allowed_parent_slugs || []).includes(parentEntry.kind_slug || ''))
    : kinds.filter(k => k.can_be_root !== false);

  // Any existing entry can be selected as a parent when creating a new record.
  const parentOptions = allEntries || [];

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 20, fontWeight: 700, color: theme.toolbar.text, marginBottom: 4 }}>
          {title || (isEdit
            ? <><EditIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 6 }} />Редактировать запись</>
            : <><AddCircleOutlineIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 6 }} />Создать запись</>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          {/* Code */}
          <label style={label}>
            Код *
            <input
              style={input} type="text" value={form.code}
              onChange={e => handleChange('code', e.target.value)}
              placeholder={profile.placeholders.code} required
              disabled={isEdit}
            />
            <div style={hint}>Уникальный идентификатор (латиница, цифры, дефис)</div>
          </label>

          {/* Name */}
          <label style={label}>
            Название *
            <input
              style={input} type="text" value={form.name}
              onChange={e => handleChange('name', e.target.value)}
              placeholder={profile.placeholders.name} required
            />
          </label>

          {/* Kind */}
          {allowedKinds.length > 0 && (
            <label style={label}>
              Тип (kind)
              <select
                style={input} value={form.kind_slug}
                onChange={e => handleChange('kind_slug', e.target.value)}
              >
                <option value="">— без типа —</option>
                {allowedKinds.map(k => (
                  <option key={k.slug} value={k.slug}>{k.name}</option>
                ))}
              </select>
            </label>
          )}

          {/* Description */}
          <label style={label}>
            Описание
            <textarea
              style={{ ...input, minHeight: 72, resize: 'vertical', fontFamily: 'inherit' }}
              value={form.description}
              onChange={e => handleChange('description', e.target.value)}
              placeholder={profile.placeholders.description}
            />
          </label>

          {/* Tags */}
          <label style={label}>
            Теги
            <input
              style={input} type="text" value={form.tags}
              onChange={e => handleChange('tags', e.target.value)}
              placeholder="backend, Мониторинг:#FF5733"
            />
            <div style={hint}>Через запятую. Формат: "имя" или "имя:#цвет"</div>
          </label>

          {/* Parent (only show when creating root-level without fixed parent) */}
          {!parentEntry && !isEdit && parentOptions.length > 0 && (
            <label style={label}>
              Родительская запись
              <select
                style={input} value={form.parent_id || ''}
                onChange={e => handleChange('parent_id', e.target.value || null)}
              >
                <option value="">— корневая запись —</option>
                {parentOptions.map(e => (
                  <option key={e.id} value={e.id}>{e.name} ({e.code})</option>
                ))}
              </select>
            </label>
          )}

          {infrastructureBindingEnabled && (
            <div style={{
              marginTop: 18, padding: 14, borderRadius: 8,
              border: `1px dashed ${theme.toolbar.buttonBorder}`,
              background: isDark ? '#111827' : '#f9fafb'
            }}>
              <div style={{ fontWeight: 600, color: theme.toolbar.text, marginBottom: 10, fontSize: 13 }}>
                <CableIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 6 }} />
                Привязка к инфраструктуре
              </div>

              {/* Binding type toggle */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                {['none', 'namespace', 'pod'].map(opt => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => handleChange('k8s_binding', opt)}
                    style={{
                      padding: '5px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                      border: `1px solid ${theme.toolbar.buttonBorder}`,
                      background: form.k8s_binding === opt
                        ? (isDark ? '#1e3a5f' : '#dbeafe')
                        : theme.toolbar.buttonBackground,
                      color: form.k8s_binding === opt
                        ? (isDark ? '#60a5fa' : '#1e40af')
                        : theme.toolbar.text,
                      fontWeight: form.k8s_binding === opt ? 600 : 400
                    }}
                  >
                    {opt === 'none' ? 'Нет' : opt === 'namespace' ? 'Namespace' : 'Pod'}
                  </button>
                ))}
              </div>

              {/* Namespace picker */}
              {form.k8s_binding === 'namespace' && (
                <div>
                  <select
                    style={input}
                    value={form.k8s_cluster_id && form.k8s_namespace
                      ? `${form.k8s_cluster_id}|${form.k8s_namespace}`
                      : ''}
                    onChange={handleNsSelect}
                    disabled={nsOptionsLoading}
                  >
                    <option value="">
                      {nsOptionsLoading ? 'Загрузка namespace...' : '— выбрать namespace —'}
                    </option>
                    {Array.from(new Set(nsOptions.map(n => n.clusterId))).map(cid => {
                      const clusterNs = nsOptions.filter(n => n.clusterId === cid);
                      const clusterName = clusterNs[0]?.clusterName || cid;
                      return (
                        <optgroup key={cid} label={`Кластер: ${clusterName}`}>
                          {clusterNs.map(n => (
                            <option key={`${cid}|${n.name}`} value={`${cid}|${n.name}`}>
                              {n.name}
                            </option>
                          ))}
                        </optgroup>
                      );
                    })}
                  </select>
                  <div style={hint}>Детали namespace (статус, лейблы, поды) отображаются в панели записи после сохранения.</div>
                </div>
              )}

              {/* Pod binding hint */}
              {form.k8s_binding === 'pod' && (
                <div style={hint}>
                  Поды с именем, содержащим код записи, будут связаны автоматически через discovery-service.
                </div>
              )}
            </div>
          )}

          {bcCapabilityBindingEnabled && (
            <div style={{
              marginTop: 18, padding: 14, borderRadius: 8,
              border: `1px dashed ${theme.toolbar.buttonBorder}`,
              background: isDark ? '#111827' : '#f9fafb'
            }}>
              <div style={{ fontWeight: 600, color: theme.toolbar.text, marginBottom: 10, fontSize: 13 }}>
                <AccountTreeIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 6 }} />
                Бизнес-способности
              </div>

              {!isEdit ? (
                <div style={{ ...hint, fontSize: 12 }}>
                  Привязать к бизнес-способностям можно после создания записи.
                </div>
              ) : bcLoading ? (
                <div style={{ ...hint, fontSize: 12 }}>Загрузка...</div>
              ) : (
                <>
                  <input
                    style={{ ...input, marginBottom: 8 }}
                    type="text"
                    placeholder="Поиск бизнес-способностей..."
                    value={bcSearch}
                    onChange={e => setBcSearch(e.target.value)}
                  />
                  <div style={{ maxHeight: 160, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {bcEntries
                      .filter(e => e.name.toLowerCase().includes(bcSearch.toLowerCase()))
                      .map(e => {
                        const selected = selectedBcIds.includes(e.id);
                        return (
                          <label
                            key={e.id}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 8,
                              padding: '5px 8px', borderRadius: 6, cursor: 'pointer',
                              background: selected ? (isDark ? '#1e3a5f' : '#dbeafe') : 'transparent',
                              color: theme.toolbar.text, fontSize: 13
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => {
                                setSelectedBcIds(prev =>
                                  selected ? prev.filter(id => id !== e.id) : [...prev, e.id]
                                );
                              }}
                            />
                            {e.name}
                          </label>
                        );
                      })
                    }
                    {bcEntries.filter(e => e.name.toLowerCase().includes(bcSearch.toLowerCase())).length === 0 && (
                      <div style={{ ...hint, fontSize: 12 }}>Нет бизнес-способностей</div>
                    )}
                  </div>
                  {selectedBcIds.length > 0 && (
                    <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {selectedBcIds.map(id => {
                        const entry = bcEntries.find(e => e.id === id);
                        return entry ? (
                          <span
                            key={id}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              padding: '3px 8px', borderRadius: 12, fontSize: 12,
                              background: isDark ? '#1e3a5f' : '#dbeafe',
                              color: isDark ? '#60a5fa' : '#1e40af'
                            }}
                          >
                            {entry.name}
                            <span
                              style={{ cursor: 'pointer', lineHeight: 1 }}
                              onClick={() => setSelectedBcIds(prev => prev.filter(i => i !== id))}
                            >×</span>
                          </span>
                        ) : null;
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {error && (
            <div style={{
              color: '#ef4444',
              background: isDark ? '#2a1f1f' : '#fee2e2',
              padding: 10, borderRadius: 6, marginTop: 14, fontSize: 13
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button
              type="submit" disabled={creating}
              style={{ ...primaryBtn, opacity: creating ? 0.6 : 1, cursor: creating ? 'not-allowed' : 'pointer' }}
            >
              {creating ? 'Сохранение...' : (submitLabel || (isEdit ? 'Сохранить' : 'Создать'))}
            </button>
            <button type="button" onClick={onClose} disabled={creating} style={secondaryBtn}>
              Отмена
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EntryModal;
